import { useState, useEffect, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  loadCaptchaEnginge,
  LoadCanvasTemplate,
  validateCaptcha,
} from "react-simple-captcha";
import { makeStyles } from "@mui/styles";
import TextField from "@mui/material/TextField";
import { Autocomplete } from "@mui/lab";
import Button from "@mui/material/Button";
import PublicWebsiteActions from "../../Actions/PublicWebsiteActions";
import PublicWebsiteConstants from "../../Constants/PublicWebsiteConstants";
import publicWebsiteStore from "../../Stores/PublicWebsiteStore";
import { useGlobalSpinnerActionsContext } from "../../Context/GlobalSpinnerContext";
import {
  useLanguageActionContext,
  useLanguageContext,
} from "../../Context/LanguageContext";
import AppConstants from "../../Constants/AppConstants";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiTextField-root": {
      margin: theme.spacing(1),
    },
  },
}));

const labelStyle = {
  fontSize: "14px",
  backgroundColor: "white",
};

export default function FaultRaiseComponent({
  equipmentDetails,
  raiseTicketSuccessCallback,
}) {
  const classes = useStyles();
  const fileInputRef = useRef();
  const {
    register,
    setFocus,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({ mode: "all" });
  const setGlobalSpinner = useGlobalSpinnerActionsContext();
  const { language, localization } = useLanguageContext();
  const UpdateLanguage = useLanguageActionContext();
  const [isLoginRequired, setIsLoginRequired] = useState(true);
  const [isMaxTicketLimitReach, setIsMaxTicketLimitReach] = useState(false);
  const [name, setName] = useState("");
  const [username, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [title, setTitle] = useState("");
  const [selectedTitle, setSelectedTitle] = useState("");
  const [inputTitle, setInputTitle] = useState("");
  const [titleList, setTitleList] = useState([]);
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("Low");
  const [category, setCategory] = useState("");
  const [categoryList, setCategoryList] = useState([]);
  const [ticketFiles, setTicketFiles] = useState([]);
  const [userEnteredCaptcha, setUserEnteredCaptcha] = useState("");
  const [captchaKey, setCaptchaKey] = useState(0);

  useEffect(() => {
    setIsLoginRequired(equipmentDetails.loginRequiredToOpenTicket);
    if (equipmentDetails.openTicketsCount >= equipmentDetails.openTicketLimit) {
      setIsMaxTicketLimitReach(true);
    } else {
      setIsMaxTicketLimitReach(false);
      getTitles(equipmentDetails.anlagegruppeID);
    }

    if (
      equipmentDetails &&
      Array.isArray(equipmentDetails.categories) &&
      equipmentDetails.categories.length > 0
    ) {
      setCategoryList(equipmentDetails.categories);
    } else {
      setCategoryList([]);
    }
    return () => {};
  }, [equipmentDetails]);

  useEffect(() => {
    UpdateLanguage(language);
    return () => {};
  }, [language]);

  useEffect(() => {
    if (!isMaxTicketLimitReach) {
      loadCaptchaEnginge(6, "purple", "white");
    }
    return () => {};
  }, [isMaxTicketLimitReach]);

  useEffect(() => {
    if (captchaKey > 0) {
      loadCaptchaEnginge(6, "purple", "white");
    }
  }, [captchaKey]);

  const getTitles = (equipmentGroupId) => {
    PublicWebsiteActions.GetDistinctTitles(equipmentGroupId);
  };

  const getTitleDescription = () => {
    if (selectedTitle != null && selectedTitle != "") {
      PublicWebsiteActions.GetTitleDescription(
        equipmentDetails.anlagegruppeID,
        selectedTitle,
      );
    } else {
      setDescription("");
    }
  };

  useEffect(() => {
    getTitleDescription();
    return () => {};
  }, [selectedTitle]);

  useEffect(() => {
    if (description != null && description != "") {
      setFocus("faultDescription");
    }
    return () => {};
  }, [description]);

  const handleFaultRaise = () => {
    let titleValue = category
      ? category
      : inputTitle
        ? inputTitle
        : selectedTitle;
    let data = {
      Id: null,
      TicketType: "EquipmentFault",
      Title: titleValue,
      Description: description,
      TicketReferenceId: equipmentDetails.anlageID
        ? equipmentDetails.anlageID.toString()
        : "",
      UserName: username,
      Password: password,
      AnlagegruppeId: AppConstants.DEFAULT_GUID,
      Priority: priority,
      Category: category,
      Files: ticketFiles,
      Language: language,
      Name: name,
      EmailAddresses: email,
      PhoneNumber: phoneNumber,
    };
    setGlobalSpinner(true);
    PublicWebsiteActions.CreateTicket(data);
  };

  function handleOnchange(event) {
    var files = [...event.target.files];
    let tempFileArray = [];
    if (files && files.length > 0) {
      files.map((file) => {
        let reader = new FileReader();
        reader.onload = () => {
          tempFileArray.push({
            Filename: file.name,
            Filecontent: reader.result,
            Filetyp: file.type,
            FileContentBytes: null,
          });
          setTicketFiles(tempFileArray);
        };
        reader.onerror = () => {};
        reader.readAsDataURL(file);
      });
    } else {
      setTicketFiles([]);
    }
  }

  const clearForm = () => {
    setUserName("");
    setPassword("");
    setTitle("");
    setDescription("");
    setPriority("Low");
    setSelectedTitle("");
    setInputTitle("");
    setUserEnteredCaptcha("");
    setEmail("");
    setPhoneNumber("");
    setCategory("");
    setName("");
    fileInputRef.current.value = null;
    setTicketFiles([]);
  };

  const updateDescription = (desc) => {
    setDescription(desc);
  };

  useEffect(() => {
    function createTicketSuccess() {
      var res = publicWebsiteStore.getCreateTicketResult();
      if (res) {
        toast.success(localization.ticketSuccess);
        clearForm();
        setCaptchaKey((prev) => prev + 1);
      }
      setGlobalSpinner(false);
      raiseTicketSuccessCallback();
    }

    function createTicketFailure() {
      var res = publicWebsiteStore.getCreateTicketResult();
      setGlobalSpinner(false);
      toast.error(res);
    }
    publicWebsiteStore.addChangeListener(
      PublicWebsiteConstants.CREATE_TICKET_SUCCESS,
      createTicketSuccess,
    );

    publicWebsiteStore.addChangeListener(
      PublicWebsiteConstants.CREATE_TICKET_FAILURE,
      createTicketFailure,
    );

    return () => {
      publicWebsiteStore.removeChangeListener(
        PublicWebsiteConstants.CREATE_TICKET_SUCCESS,
        createTicketSuccess,
      );

      publicWebsiteStore.removeChangeListener(
        PublicWebsiteConstants.CREATE_TICKET_FAILURE,
        createTicketFailure,
      );
    };
  }, [localization]);

  useEffect(() => {
    function getTitleSuccess() {
      var res = publicWebsiteStore.getTitleResult();
      if (res != null && res != "" && res.length == undefined) {
        setTitleList([res.toString()]);
      } else if (res.length > 0) {
        setTitleList(res);
      }
      setGlobalSpinner(false);
    }

    function getTitleFailure() {
      var res = publicWebsiteStore.getTitleResult();
      setGlobalSpinner(false);
      toast.error(res);
    }
    publicWebsiteStore.addChangeListener(
      PublicWebsiteConstants.GET_TITLE_SUCCESS,
      getTitleSuccess,
    );

    publicWebsiteStore.addChangeListener(
      PublicWebsiteConstants.GET_TITLE_FAILURE,
      getTitleFailure,
    );

    return () => {
      publicWebsiteStore.removeChangeListener(
        PublicWebsiteConstants.GET_TITLE_SUCCESS,
        getTitleSuccess,
      );

      publicWebsiteStore.removeChangeListener(
        PublicWebsiteConstants.GET_TITLE_FAILURE,
        getTitleFailure,
      );
    };
  }, []);

  useEffect(() => {
    function getTitleDescriptionSuccess() {
      var res = publicWebsiteStore.getDescriptionResult();
      if (res) {
        updateDescription(res.description);
      }
      setGlobalSpinner(false);
    }

    function getTitleDescriptionFailure() {
      var res = publicWebsiteStore.getDescriptionResult();
      setGlobalSpinner(false);
      toast.error(res);
    }
    publicWebsiteStore.addChangeListener(
      PublicWebsiteConstants.GET_TITLE_DESCRIPTION_SUCCESS,
      getTitleDescriptionSuccess,
    );

    publicWebsiteStore.addChangeListener(
      PublicWebsiteConstants.GET_TITLE_DESCRIPTION_FAILURE,
      getTitleDescriptionFailure,
    );

    return () => {
      publicWebsiteStore.removeChangeListener(
        PublicWebsiteConstants.GET_TITLE_DESCRIPTION_SUCCESS,
        getTitleDescriptionSuccess,
      );

      publicWebsiteStore.removeChangeListener(
        PublicWebsiteConstants.GET_TITLE_DESCRIPTION_FAILURE,
        getTitleDescriptionFailure,
      );
    };
  }, []);

  // Custom phone number validation function
  const validatePhoneNumber = useCallback(
    (value) => {
      if (email) return true; // If email is filled, don't validate phone number
      //const phoneRegex = /^[+]?[0-9]{10,15}$/;
      //return phoneRegex.test(value) || "Invalid phone number"; // Phone validation
      return value == "" ? localization.emailOrPhoneRequired : true;
    },
    [phoneNumber, email],
  );

  // Custom email validation function
  const validateEmail = useCallback(
    (value) => {
      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
      if (
        (phoneNumber && value.trim() != "" && emailRegex.test(value.trim())) ||
        (phoneNumber && value.trim() == "")
      ) {
        return true; // If phone number is filled, don't validate email
      }
      return emailRegex.test(value) || localization.pleaseEnterValidEmail; // Email validation
    },
    [email, phoneNumber],
  );

  const validateFileInput = useCallback(() => {
    if (equipmentDetails.isPhotoNeededForTicket) {
      return ticketFiles.length == 0 ? false : true;
    }
    return true;
  }, [ticketFiles]);

  return (
    <>
      {isMaxTicketLimitReach ? (
        <>
          <div className="text-center">
            <h3>{localization.maxLimitReached}</h3>
          </div>
        </>
      ) : (
        <>
          <div className="text-center">
            <h3>{localization.submitFormInfo}</h3>
          </div>
          <div className="failtRaiseFormContainer card">
            <form
              className={classes.root + " faultReportForm"}
              noValidate
              autoComplete="off"
              onSubmit={handleSubmit(handleFaultRaise)}
            >
              {/* Category field: only show if categories exist */}
              {categoryList.length > 0 && (
                <div className="row m-0">
                  <TextField
                    select
                    className="categorySelect"
                    error={!!errors.category}
                    id="category"
                    label={localization.ticketCategory}
                    fullWidth
                    value={category}
                    helperText={errors.category && errors.category?.message}
                    variant="outlined"
                    SelectProps={{
                      native: true,
                    }}
                    InputLabelProps={{ style: labelStyle, shrink: true }}
                    {...register("category")}
                    InputProps={{ className: "categorySelect" }}
                    onChange={(e) => {
                      setCategory(e.target.value);
                    }}
                  >
                    <option value="">{localization.select}</option>
                    {categoryList.map((cat, index) => {
                      return (
                        <option key={index} value={cat}>
                          {cat}
                        </option>
                      );
                    })}
                  </TextField>
                </div>
              )}

              {/* Title field: only show if no category is selected */}
              {(!category || category === "") && (
                <div className="row m-0">
                  <Autocomplete
                    freeSolo
                    id="title"
                    className="titleAutocomplete"
                    value={selectedTitle}
                    fullWidth
                    options={titleList.map((title) => title)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={localization.ticketTitle}
                        error={!!errors.Title}
                        helperText={errors.Title && errors.Title?.message}
                        margin="normal"
                        variant="outlined"
                        InputLabelProps={{ style: labelStyle }}
                        {...register("Title", {
                          required: localization.titleRequired,
                        })}
                        InputProps={{ ...params.InputProps }}
                      />
                    )}
                    onChange={(e, newValue) => {
                      setSelectedTitle(newValue);
                    }}
                    inputValue={inputTitle}
                    onInputChange={(event, newInputValue) => {
                      setInputTitle(newInputValue);
                    }}
                  />
                </div>
              )}
              <div className="row m-0">
                <TextField
                  error={!!errors.faultDescription}
                  id="faultDescription"
                  label={localization.description}
                  multiline
                  fullWidth
                  value={description}
                  variant="outlined"
                  helperText={errors.faultDescription?.message}
                  InputLabelProps={{ style: labelStyle }}
                  {...register("faultDescription", {
                    required: localization.descriptionRequired,
                  })}
                  onChange={(e) => {
                    setDescription(e.target.value);
                  }}
                />
              </div>

              {isLoginRequired && (
                <>
                  <div className="row m-0">
                    <TextField
                      error={!!errors.username}
                      id="username"
                      label={localization.username}
                      fullWidth
                      value={username}
                      helperText={errors.username && errors.username?.message}
                      variant="outlined"
                      InputLabelProps={{ style: labelStyle }}
                      {...register("username", {
                        required: localization.usernameRequired,
                      })}
                      onChange={(e) => {
                        setUserName(e.target.value);
                      }}
                    />
                  </div>
                  <div className="row m-0">
                    <TextField
                      type="password"
                      error={!!errors.password}
                      id="password"
                      label={localization.password}
                      fullWidth
                      value={password}
                      helperText={errors.password && errors.password?.message}
                      variant="outlined"
                      InputLabelProps={{ style: labelStyle }}
                      {...register("password", {
                        required: localization.passwordRequired,
                      })}
                      onChange={(e) => {
                        setPassword(e.target.value);
                      }}
                    />
                  </div>
                </>
              )}

              {!isLoginRequired && (
                <>
                  <div className="row m-0">
                    <TextField
                      id="name"
                      label={localization.name}
                      fullWidth
                      value={name}
                      variant="outlined"
                      InputLabelProps={{ style: labelStyle }}
                      onChange={(e) => {
                        setName(e.target.value);
                      }}
                    />
                  </div>

                  <div className="row m-0">
                    <TextField
                      type="number"
                      error={!!errors.phoneNumber}
                      id="phoneNumber"
                      label={localization.phoneNumber}
                      fullWidth
                      value={phoneNumber}
                      helperText={
                        errors.phoneNumber && errors.phoneNumber?.message
                      }
                      variant="outlined"
                      InputLabelProps={{ style: labelStyle }}
                      {...register("phoneNumber", {
                        required: !email
                          ? localization.emailOrPhoneRequired
                          : false,
                        validate: validatePhoneNumber,
                      })}
                      onChange={(e) => {
                        setPhoneNumber(e.target.value);
                      }}
                    />
                  </div>

                  <div className="row m-0">
                    <TextField
                      type="email"
                      error={!!errors.email}
                      id="email"
                      label={localization.email}
                      fullWidth
                      value={email}
                      helperText={errors.email && errors.email?.message}
                      variant="outlined"
                      InputLabelProps={{ style: labelStyle }}
                      {...register("email", {
                        required: !phoneNumber
                          ? localization.emailOrPhoneRequired
                          : false,
                        validate: validateEmail,
                      })}
                      onChange={(e) => {
                        setEmail(e.target.value);
                      }}
                    />
                  </div>
                </>
              )}

              <div className="row m-0">
                <TextField
                  select
                  className="prioritySelect"
                  error={!!errors.priority}
                  id="priority"
                  label={localization.priority}
                  fullWidth
                  value={priority}
                  helperText={errors.priority && errors.priority?.message}
                  variant="outlined"
                  SelectProps={{
                    native: true,
                  }}
                  InputLabelProps={{ style: labelStyle }}
                  {...register("priority", {
                    required: localization.priorityRequired,
                  })}
                  InputProps={{ className: "prioritySelect" }}
                  onChange={(e) => {
                    setPriority(e.target.value);
                  }}
                >
                  <option key="low" value="Low">
                    {localization.low}
                  </option>
                  <option key="medium" value="Medium">
                    {localization.medium}
                  </option>
                  <option key="high" value="High">
                    {localization.high}
                  </option>
                </TextField>
              </div>

              <div className="row m-0">
                <TextField
                  className="faultImageFileInputCls"
                  error={!!errors.fileUpload}
                  inputRef={fileInputRef}
                  type="file"
                  id="fileUpload"
                  label={localization.chooseImage}
                  fullWidth
                  variant="outlined"
                  inputProps={{
                    multiple: true,
                    accept: "image/*",
                    capture: "environment",
                  }}
                  InputLabelProps={{ style: labelStyle }}
                  helperText={errors.fileUpload && errors.fileUpload?.message}
                  {...register("fileUpload", {
                    required:
                      equipmentDetails.isPhotoNeededForTicket &&
                      ticketFiles.length == 0
                        ? localization.imageIsRequired
                        : false,
                    validate: { validateFileInput },
                  })}
                  onChange={handleOnchange}
                />
              </div>
              <div className="row m-0">
                <div style={{ margin: "8px" }}>
                  <LoadCanvasTemplate
                    key={captchaKey}
                    reloadText={localization.reloadCaptcha}
                  />
                </div>
              </div>
              <div className="row m-0">
                <TextField
                  error={!!errors.userEnteredCaptcha}
                  id="userEnteredCaptcha"
                  label={localization.enterCaptcha}
                  fullWidth
                  value={userEnteredCaptcha}
                  helperText={
                    errors.userEnteredCaptcha &&
                    errors.userEnteredCaptcha?.message
                  }
                  variant="outlined"
                  InputLabelProps={{ style: labelStyle }}
                  {...register("userEnteredCaptcha", {
                    required: localization.captchaRequired,
                    validate: () => {
                      return (
                        (userEnteredCaptcha != "" &&
                          validateCaptcha(userEnteredCaptcha, false) == true) ||
                        localization.captchaDontMatch
                      );
                    },
                  })}
                  onChange={(e) => {
                    setUserEnteredCaptcha(e.target.value);
                  }}
                />
              </div>
              <div className="row m-0">
                <div className="text-center w-100">
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                  >
                    {localization.submit}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </>
      )}
    </>
  );
}
